import React, { useEffect, useMemo, useState } from 'react'

import { Alert, Snackbar } from '@mui/material'
import { CheckCircle, Info, Warning, Error } from '@mui/icons-material'

import { ToastProps, TToastSeverity } from './types'

const DEFAULT_TOAST_DURATION = 5000

const SEVERITY_STYLE: Record<TToastSeverity, { color: string; backgroundColor: string }> = {
  success: {
    color: 'white',
    backgroundColor: 'success.main',
  },
  info: {
    color: 'white',
    backgroundColor: 'primary.main',
  },
  warning: {
    color: 'white',
    backgroundColor: 'warning.main',
  },
  error: {
    color: 'white',
    backgroundColor: 'error.main',
  },
}

const ICON_STYLE = { color: 'white' }

const Toast: React.FC<ToastProps> = ({ toast, autoHideDuration, onExited, ...props }) => {
  const { key, message, options } = toast
  const [open, setOpen] = useState(false)

  const duration = useMemo(() => {
    if (autoHideDuration) return autoHideDuration
    if (options.duration) return options.duration

    return DEFAULT_TOAST_DURATION
  }, [autoHideDuration, options.duration])

  const handleOnClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }

    setOpen(false)
  }

  useEffect(() => {
    setOpen(true)
  }, [])

  return (
    <Snackbar
      {...props}
      key={key}
      open={open}
      onClose={handleOnClose}
      TransitionProps={{ onExited }}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={duration}
      style={{ position: 'relative' }}
    >
      <Alert
        severity={options.severity}
        onClose={handleOnClose}
        sx={{
          borderRadius: 2,
          alignItems: 'center',
          width: '100%',
          ...SEVERITY_STYLE[options.severity],
        }}
        iconMapping={{
          success: <CheckCircle style={ICON_STYLE} />,
          info: <Info style={ICON_STYLE} />,
          warning: <Warning style={ICON_STYLE} />,
          error: <Error style={ICON_STYLE} />,
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  )
}

export default Toast
